import type { DetailedHTMLProps, InputHTMLAttributes } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { clearBadgeSource, setBadgeSource } from '@/renderer/utils/appBadge';
import { createCoalescedRunner } from '@/renderer/utils/coalesce';
import { useReconcileOnForeground } from '@/renderer/utils/hooks/useReconcileOnForeground';
import { useReconcileOnReconnect } from '@/renderer/utils/hooks/useReconcileOnReconnect';
import { useRefUpdating } from '@/renderer/utils/hooks/useRefUpdating';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { IS_SERVER } from '@/renderer/utils/ssr';
import { useIdleContext } from '@/renderer/utils/useIdle';
import useMe, { useAuthToken } from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';
import useWebSocket, { WS_BASE_URL } from '@/renderer/utils/useWebSocket2';

// Trailing debounce for the server seed/reconcile fetch. Mount, WS reconnect, and
// foreground can all fire together on a cold open; coalesce them into ONE fetch.
const SEED_RECONCILE_DEBOUNCE_MS = 300;
// Spread the reconcile across clients (#2059): mount/reconnect/foreground are
// correlated events fleet-wide (a release push wakes everyone at once), so a
// fixed 300ms delay just relocates the spike. Land uniformly in 0.3-1.5s.
const SEED_RECONCILE_JITTER_MS = 1200;

export interface WebSocketUnreadCounterProps<T = unknown> {
  dataName: string;
  dataPath: string;
  dataCb?: (
    event: MessageEvent,
    data: API.SubscriptionResponse<T>,
    setCount: (value: React.SetStateAction<number>) => void,
    mapRef: React.MutableRefObject<Map<string | number, number>>,
    me: API.User | null | undefined,
  ) => void;
  dataNavCb?: (
    urlPathname: string,
    setCount: (value: React.SetStateAction<number>) => void,
    mapRef: React.MutableRefObject<Map<string | number, number>>,
    me: API.User | null | undefined,
  ) => void;
  // Server-backed seed/reconcile. When provided, the counter is seeded from this
  // bounded server unread count on mount, and reconciled to it on WS reconnect and
  // on returning to the foreground, so the sidebar number matches the server push
  // badge (which uses the same count) instead of being a session-only useState that
  // starts at 0 on every load. Live WS increments still nudge it between reconciles,
  // and viewing the counter's own page still clears it to 0. Best-effort: a failed
  // fetch leaves the current count untouched.
  getUnreadCount?: () => Promise<number>;
}

export default function WebSocketUnreadCounter<T>({
  dataName = 'directMessageReceipt/created',
  dataPath = '/support-chat',
  dataCb,
  dataNavCb,
  getUnreadCount,
  children,
  ...props
}: WebSocketUnreadCounterProps<T> &
  DetailedHTMLProps<InputHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>) {
  const [count, setCount] = useState<number>(0);

  const { urlPathname } = usePageContext();
  const authToken = useAuthToken();
  const mapRef = useRef<Map<number | string, number>>(new Map());
  const me = useMe();

  const idle = useIdleContext();
  // "Backgrounded" = NOT `idle.present` (the window is hidden or unfocused).
  // Deliberately NARROWER than `inactive` (which is `idle || !visible ||
  // !focused`): sitting still reading a live blast trips the idle timer (~10s
  // desktop / ~25s mobile) while the page is still very much on screen and
  // focused. Being ON the counter's own page and visible+focused must count as
  // reading, so BOTH increment guards below key on this, not on `inactive` —
  // otherwise the badge climbed with every new message while the user was
  // actively watching the page. #1885 fixed the plain guard; the `dataCb`
  // guard had the same bug (still keyed on the idle timer) until that was
  // noticed and fixed too. `idle.present` is computed once in useIdle.tsx
  // (#2072) so this can't drift from the other present-vs-inactive gates
  // (notifications, read-ack). The recovery effects further below key on this
  // SAME derived value (not a second, differently-timed signal like
  // `idle.inactive`) so every increment either guard makes has exactly one
  // matching decrement (#2058).
  const backgrounded = !idle.present;
  const backgroundedRef = useRef(backgrounded);

  // Each counter instance (support-chat, release-center, ...) is one source that
  // feeds the shared home-screen badge, keyed by its dataPath. The aggregator
  // sums every source so the badge reflects the combined unread total instead of
  // whichever counter updated the badge last (issue #1347).
  const badgeKey = dataPath;

  useEffect(() => {
    backgroundedRef.current = backgrounded;
  }, [backgrounded]);

  const onUnreadMessage = useCallback((event: MessageEvent, data: API.SubscriptionResponse<T>) => {
    // We don't use urlPathname so it prevents this callback from being re-bound on navigation
    const pathname = IS_SERVER ? urlPathname : location.pathname;

    // On the counter's own page AND visible+focused = reading, so don't grow the
    // badge (idle alone no longer counts). While off-page, or while the page is
    // hidden/unfocused, either let `dataCb` do its own (per-conversation) bookkeeping,
    // or accumulate into mapRef so the backgrounded->present decrement (or navigating
    // to the page) can net it back out on return without going negative. See
    // backgroundedRef above (#1885).
    if (pathname === dataPath && !backgroundedRef.current) {
      return;
    }

    if (dataCb) {
      dataCb(event, data, setCount, mapRef, me);
    } else {
      setCount((oldCount) => {
        mapRef.current.set(dataPath, oldCount + 1);

        return oldCount + 1;
      });
    }
  }, []);

  useEffect(() => {
    if (dataNavCb) {
      dataNavCb(urlPathname, setCount, mapRef, me);
    } else if (urlPathname === dataPath) {
      mapRef.current.delete(dataPath);
      setCount(0);
    }
  }, [urlPathname]);

  // Net out an accumulated count once `backgrounded` clears — the SAME signal
  // the `dataCb` increment guard above uses. Support-chat's dataCb manages its
  // own map keys (one per conversation path), so this stays keyed on
  // `location.pathname` to match that convention; it's a no-op for a
  // dataCb-less counter, which nets out via the effect below instead.
  useUpdateEffect(() => {
    if (dataCb && !backgrounded) {
      const mapCount = mapRef.current.get(location.pathname);

      if (mapCount) {
        mapRef.current.delete(location.pathname);
        setCount((oldCount) => Math.max(oldCount - mapCount, 0));
      }
    }
  }, [backgrounded]);

  // Give the badge a recovery path while the member stays ON the counter's own
  // page — no navigation required (#2058). Keys on the SAME signal the
  // increment guard above uses (`backgrounded`, not the broader idle timer)
  // and the SAME map key (`dataPath`, not `location.pathname`), so every
  // increment this guard makes has exactly one matching decrement: an
  // increment that fired while genuinely backgrounded, or one that fired only
  // because `visible`/`focused` were momentarily stale, both net out the
  // moment presence is re-established. The `urlPathname === dataPath` guard
  // keeps this from firing just because focus returned to the app while the
  // member is browsing a different page. Preserves #1885: only a real exit
  // from "backgrounded" clears anything, never idle alone.
  useUpdateEffect(() => {
    if (!dataCb && !backgrounded && urlPathname === dataPath) {
      const mapCount = mapRef.current.get(dataPath);

      if (mapCount) {
        mapRef.current.delete(dataPath);
        setCount((oldCount) => Math.max(oldCount - mapCount, 0));
      }
    }
  }, [backgrounded, urlPathname]);

  const ws = useWebSocket(WS_BASE_URL, {
    connect: !!authToken,
    subscriptions: {
      [dataName]: onUnreadMessage,
    },
  });

  // ── Server-backed seed / reconcile ──────────────────────────────────────────
  // Without this the counter is a session-only useState that starts at 0 on every
  // load, so the active (in-app) sidebar number never matched the inactive (push)
  // badge, which the server sets from a bounded server unread count. When a fetcher
  // is provided we seed from that SAME server count on mount and reconcile to it on
  // WS reconnect + foreground, so the green sidebar number, the focused app badge,
  // and the push badge are one number for the same state. Live WS increments still
  // nudge it up between reconciles (an instant new-release bump, zero server load),
  // and viewing the page still clears it — so this adds no release-time query
  // fan-out (#1801): reconciles only fire on mount / reconnect / foreground.
  //
  // Admin/staff support-chat sidebars count DISTINCT unread threads (dataCb), which
  // is not the raw inbound-DM total this would seed, so seeding is limited to
  // sources without a custom dataCb (release center) and to regular members.
  const seedEnabled =
    !!getUnreadCount && (!dataCb || (!!me?.role && me.role !== 'admin' && me.role !== 'staff'));
  const getUnreadCountRef = useRefUpdating(getUnreadCount);
  const seedRunnerRef = useRef(
    createCoalescedRunner(SEED_RECONCILE_DEBOUNCE_MS, SEED_RECONCILE_JITTER_MS),
  );

  // Apply an authoritative server count. On the counter's own page while
  // visible+focused the member is reading, so these are read → 0; otherwise reseat
  // the base to the server truth and drop any accumulated session delta so the
  // idle→active net-out can't subtract stale increments from a fresh seed.
  const applyServerCount = useCallback(
    (serverCount: number) => {
      mapRef.current.delete(dataPath);
      const reading = !IS_SERVER && location.pathname === dataPath && !backgroundedRef.current;
      setCount(reading ? 0 : Math.max(serverCount, 0));
    },
    [dataPath],
  );
  const applyServerCountRef = useRefUpdating(applyServerCount);

  useEffect(() => {
    if (!seedEnabled || !authToken) {
      return;
    }
    const runner = seedRunnerRef.current;
    const unregister = runner.register(async () => {
      try {
        const value = await getUnreadCountRef.current?.();
        if (typeof value === 'number' && Number.isFinite(value)) {
          applyServerCountRef.current(value);
        }
      } catch {
        // Best-effort: keep the current count if the fetch fails.
      }
    });
    // Seed once now (mount / sign-in).
    runner.schedule();
    return unregister;
  }, [seedEnabled, authToken]);

  const scheduleReconcile = useCallback(() => {
    seedRunnerRef.current.schedule();
  }, []);

  // Reconnect + foreground reconcile (mirrors usePendingPicksCount / the reconcile
  // primitive). schedule() no-ops when nothing is registered, so this is free for
  // sources without a fetcher and for signed-out users.
  useReconcileOnReconnect(seedEnabled && !!authToken && ws.ready, scheduleReconcile);
  useReconcileOnForeground(scheduleReconcile);

  // Report this source's count into the shared badge aggregator; drop it on
  // unmount so a removed counter doesn't leave a stale contribution behind.
  useEffect(() => {
    setBadgeSource(badgeKey, count);
  }, [badgeKey, count]);

  useEffect(() => () => clearBadgeSource(badgeKey), [badgeKey]);

  return (
    <a {...props} data-count={count ? count : undefined}>
      {children}
    </a>
  );
}
